// Quiz construction helpers.

import { STATES, OPENED_STATES } from '../data/states.js';

const QUESTIONS_PER_ROUND = 10;

export const MODES = {
  capitals: {
    id: 'capitals',
    name: 'Capitals',
    description: 'Name the capital city of each state.',
  },
};

export const SCOPES = {
  opened: { id: 'opened', name: '15 OpenEd states', states: OPENED_STATES },
  all: { id: 'all', name: 'All 50 states', states: STATES },
};

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickDistractors(allStates, correctState, getValue, n = 3) {
  // Pick n distractors from allStates that aren't the correct one,
  // and whose value (e.g., capital name) differs from the correct value.
  const candidates = allStates.filter(s => s.abbr !== correctState.abbr && getValue(s) !== getValue(correctState));
  return shuffle(candidates).slice(0, n);
}

function buildCapitalsQuestion(state, allStates) {
  const distractors = pickDistractors(allStates, state, s => s.capital);
  const choices = shuffle([state, ...distractors]).map(s => ({
    id: s.abbr,
    label: s.capital,
    isCorrect: s.abbr === state.abbr,
  }));
  return {
    type: 'capitals',
    state,
    promptLabel: 'What is the capital of',
    promptText: state.name,
    choices,
  };
}

function buildShapesQuestion(state, allStates) {
  const distractors = pickDistractors(allStates, state, s => s.name);
  const choices = shuffle([state, ...distractors]).map(s => ({
    id: s.abbr,
    label: s.name,
    isCorrect: s.abbr === state.abbr,
  }));
  return {
    type: 'shapes',
    state,
    promptLabel: 'Which state has',
    promptText: 'this shape?',
    choices,
  };
}

function buildFlagsQuestion(state, allStates) {
  const distractors = pickDistractors(allStates, state, s => s.name);
  const choices = shuffle([state, ...distractors]).map(s => ({
    id: s.abbr,
    label: s.name,
    isCorrect: s.abbr === state.abbr,
  }));
  return {
    type: 'flags',
    state,
    promptLabel: 'Whose flag is',
    promptText: 'this?',
    choices,
  };
}

const BUILDERS = {
  capitals: buildCapitalsQuestion,
  shapes: buildShapesQuestion,
  flags: buildFlagsQuestion,
};

// Pick states with a slight bias toward OpenEd states so they show up in roughly
// 1 in 3 questions, which makes the OpenEd moment feel special but not constant.
function selectQuestionStates(scopeStates, count) {
  const opened = scopeStates.filter(s => s.isOpenEd);
  const others = scopeStates.filter(s => !s.isOpenEd);

  // If the scope is opened-only, just shuffle.
  if (others.length === 0) {
    return shuffle(opened).slice(0, count);
  }

  const targetOpenedCount = Math.min(opened.length, Math.round(count / 3));
  const targetOtherCount = count - targetOpenedCount;
  const picks = [
    ...shuffle(opened).slice(0, targetOpenedCount),
    ...shuffle(others).slice(0, targetOtherCount),
  ];
  return shuffle(picks);
}

export function buildQuiz({ mode, scope }) {
  const scopeStates = SCOPES[scope].states;
  const builder = BUILDERS[mode];
  const stateSelections = selectQuestionStates(scopeStates, QUESTIONS_PER_ROUND);
  return stateSelections.map(state => builder(state, STATES));
}

export const QUESTIONS_COUNT = QUESTIONS_PER_ROUND;
